import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { ArrowUpRight, ArrowRight, Mail, Phone, MapPin, Globe, Upload, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { INQUIRY_TYPES, PREFERRED_MODES, COUNTRIES } from "@/lib/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  full_name: "",
  company: "",
  email: "",
  phone: "",
  country: "",
  inquiry_type: "",
  summary: "",
  preferred_mode: "",
  document_name: "",
};

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const { hash } = useLocation();

  useEffect(() => {
    if (hash === "#contact-form") {
      setTimeout(() => {
        const el = document.getElementById("contact-form");
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }, 100);
    }
  }, [hash]);

  const onChange = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (file) onChange("document_name", file.name);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.summary || !form.inquiry_type || !form.preferred_mode) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Inquiry submitted successfully. Our team will be in touch.");
      setSubmitted(true);
      setForm(EMPTY);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const fieldBase =
    "w-full rounded-xl bg-[#F8F9FA] border border-gray-200 px-4 py-3 text-sm focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#7B2CBF] focus:border-transparent transition-all";

  return (
    <div data-testid="contact-page">
      {/* HERO */}
      <section className="relative bg-white pt-6 md:pt-10 pb-10 md:pb-14 overflow-hidden">
        <div className="absolute inset-0 grid-pattern" />
        <div className="glow-blob bg-[#7B2CBF]/20 w-[500px] h-[500px] -top-32 -right-32 pulse-glow" />
        <div className="container-ngxp relative z-10 max-w-3xl">
          <span className="overline">Contact</span>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] text-[#0A0B10]">
            Let's Build Your <span className="gradient-text-glow">Next-Generation Technology System</span>
          </h1>
          <p className="mt-6 text-lg text-gray-600 leading-relaxed">
            Have a complex business challenge, enterprise technology requirement, AI initiative, digital twin idea, simulation project, or strategic partnership opportunity? NGXP Technologies is ready to discuss how we can help.
          </p>
          <p className="mt-4 text-base text-gray-500 leading-relaxed">
            Share your requirement with us. Our team will understand your objective, review the opportunity, and guide you toward the right technology direction.
          </p>
          <div className="mt-8">
            <a href="#contact-form" className="btn-primary-gradient inline-flex" data-testid="contact-hero-cta">
              Start a Strategic Discussion <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* SECTION 2: CONVERSATION INTRO */}
      <section className="bg-white py-14 md:py-20">
        <div className="container-ngxp">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-6 reveal">
              <span className="overline">Get in Touch</span>
              <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-[#0A0B10]">
                Start the Conversation with <span className="gradient-text-glow">NGXP</span>
              </h2>
              <p className="mt-6 text-gray-600 leading-relaxed font-medium">Every strong technology solution begins with the right discussion.</p>
              <p className="mt-4 text-gray-600 leading-relaxed">Whether you are planning a new enterprise platform, upgrading existing systems, exploring AI capabilities, building a digital twin, creating immersive training, or evaluating a long-term technology partner, NGXP can help you understand what is possible and how to move forward.</p>
            </div>
            <div className="lg:col-span-6 reveal">
              <p className="text-xs font-bold text-gray-400 uppercase tracking-[0.2em] mb-6">We welcome conversations with:</p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  "Enterprise clients",
                  "Business leaders",
                  "Operations teams",
                  "Technology heads",
                  "Innovation teams",
                  "Investors",
                  "Strategic partners",
                  "Global organizations",
                  "Startups with complex product ideas",
                  "Industry leaders exploring next-generation systems",
                ].map((item, i) => (
                  <div key={i} className="contact-item flex items-center gap-2" style={{ animationDelay: `${i * 0.07}s` }}>
                    <CheckCircle2 className="contact-icon w-4 h-4 text-[#7B2CBF] shrink-0" />
                    <span className="contact-text text-sm text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 3: HOW WE CAN HELP */}
      <section className="bg-[#F8F9FA] py-14 md:py-20">
        <div className="container-ngxp">
          <div className="max-w-2xl reveal">
            <span className="overline">Scope</span>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-[#0A0B10]">
              How We Can <span className="gradient-text-glow">Help</span>
            </h2>
            <p className="mt-4 text-gray-600">You can contact NGXP Technologies for any of the following:</p>
          </div>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 reveal">
            {[
              "AI-native enterprise platforms",
              "Agentic AI and AI assistant systems",
              "RAG, Graph RAG, and knowledge intelligence systems",
              "Digital twin and real-time monitoring systems",
              "Computer vision and visual intelligence systems",
              "IoT, embedded, and edge intelligence solutions",
              "AI + 3D simulation systems",
              "Immersive 3D, AR, VR, MR, and XR training systems",
              "Command centers and decision intelligence dashboards",
              "Intelligent workflow automation",
              "Enterprise system modernization",
              "Strategic technology partnerships",
              "Investment, collaboration, or joint venture discussions",
            ].map((item, i) => (
              <div
                key={i}
                className="group flex items-start gap-3 bg-white rounded-xl border border-gray-200 p-4 transition-all duration-300 hover:border-[#00A8E8] hover:shadow-lg hover:-translate-y-1 hover:bg-gradient-to-br hover:from-white hover:to-[#e8f7fd]"
                style={{ animationDelay: `${i * 60}ms` }}
              >
                <ArrowRight className="w-4 h-4 text-[#00A8E8] shrink-0 mt-0.5 transition-transform duration-300 group-hover:translate-x-1" />
                <span className="text-sm text-gray-700 leading-relaxed group-hover:text-[#0A0B10] transition-colors duration-300">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 4: FORM + CONTACT INFO */}
      <section id="contact-form" className="bg-white py-12 md:py-16">
        <div className="container-ngxp grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* FORM */}
          <div className="lg:col-span-7 ngxp-card !p-8 md:!p-10">
            {submitted ? (
              <div className="text-center py-10" data-testid="contact-success">
                <div className="inline-flex w-16 h-16 rounded-2xl bg-gradient-to-br from-[#00A8E8] to-[#7B2CBF] items-center justify-center text-white mb-5">
                  <CheckCircle2 className="w-8 h-8" />
                </div>
                <h3 className="font-display text-2xl font-bold">Thank you for reaching out.</h3>
                <p className="mt-3 text-gray-600 max-w-md mx-auto">Our team has received your inquiry and will respond within one business day.</p>
                <button onClick={() => setSubmitted(false)} className="mt-6 btn-secondary-light" data-testid="contact-submit-another">Submit another inquiry</button>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="space-y-5" data-testid="contact-form">
                <div>
                  <h3 className="font-display text-2xl font-bold text-[#0A0B10]">Tell Us About Your Requirement</h3>
                  <p className="text-sm text-gray-500 mt-1">Please share a few details so we can understand your need and respond with the right direction.</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label="Full Name *">
                    <input className={fieldBase} value={form.full_name} onChange={(e) => onChange("full_name", e.target.value)} required data-testid="contact-input-name" />
                  </Field>
                  <Field label="Company / Organization Name">
                    <input className={fieldBase} value={form.company} onChange={(e) => onChange("company", e.target.value)} data-testid="contact-input-company" />
                  </Field>
                  <Field label="Business Email *">
                    <input type="email" className={fieldBase} value={form.email} onChange={(e) => onChange("email", e.target.value)} required data-testid="contact-input-email" />
                  </Field>
                  <Field label="Phone / WhatsApp">
                    <input className={fieldBase} value={form.phone} onChange={(e) => onChange("phone", e.target.value)} data-testid="contact-input-phone" />
                  </Field>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label="Country / Region">
                    <select className={fieldBase} value={form.country} onChange={(e) => onChange("country", e.target.value)} data-testid="contact-select-country">
                      <option value="">Select country...</option>
                      {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </Field>
                  <Field label="Type of Inquiry *">
                    <select className={fieldBase} value={form.inquiry_type} onChange={(e) => onChange("inquiry_type", e.target.value)} required data-testid="contact-select-inquiry">
                      <option value="">Select type...</option>
                      {INQUIRY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </Field>
                </div>

                <Field label="Project / Requirement Summary *">
                  <textarea
                    className={`${fieldBase} min-h-[140px] resize-y`}
                    value={form.summary}
                    onChange={(e) => onChange("summary", e.target.value)}
                    placeholder="Describe your operational challenge, system idea, or partnership interest..."
                    required
                    data-testid="contact-textarea-summary"
                  />
                </Field>

                <Field label="Preferred Mode of Discussion *">
                  <div className="flex flex-wrap gap-2">
                    {PREFERRED_MODES.map((m) => (
                      <button
                        type="button"
                        key={m}
                        onClick={() => onChange("preferred_mode", m)}
                        data-testid={`contact-mode-${m.split(" ")[0].toLowerCase()}`}
                        className={`px-4 py-2 rounded-full text-sm font-medium border transition-all ${
                          form.preferred_mode === m
                            ? "bg-[#0A0B10] text-white border-[#0A0B10]"
                            : "bg-white text-gray-700 border-gray-200 hover:border-[#7B2CBF]"
                        }`}
                      >
                        {m}
                      </button>
                    ))}
                  </div>
                </Field>

                <Field label="Upload Supporting Document (optional)">
                  <label className="flex items-center gap-3 px-4 py-3 rounded-xl bg-white border border-dashed border-gray-300 cursor-pointer hover:border-[#7B2CBF] transition-colors" data-testid="contact-upload-label">
                    <Upload className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-600">{form.document_name || "Click to attach a file (PDF, DOC, image)"}</span>
                    <input type="file" className="hidden" onChange={onFile} data-testid="contact-upload-input" />
                  </label>
                </Field>

                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary-gradient w-full sm:w-auto disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid="contact-submit"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowUpRight className="w-4 h-4" />}
                  {loading ? "Submitting..." : "Submit Inquiry"}
                </button>
              </form>
            )}
          </div>

          {/* CONTACT INFO */}
          <div className="lg:col-span-5 space-y-6">
            <div className="ngxp-card !bg-[#0A0B10] !text-white !border-white/10 relative overflow-hidden">
              <div className="absolute inset-0 dark-grid-pattern opacity-40" />
              <div className="relative z-10">
                <h3 className="font-display text-2xl font-bold">NGXP Technologies</h3>
                <p className="text-sm text-white/60 mt-2">Reach our team through any channel below.</p>

                <ul className="mt-6 space-y-5 text-sm">
                  <li className="flex gap-3">
                    <Mail className="w-4 h-4 mt-1 text-[#00A8E8] shrink-0" />
                    <a href="mailto:info@ngxptechnologies.com" className="hover:text-white text-white/80" data-testid="contact-info-email">info@ngxptechnologies.com</a>
                  </li>
                  <li className="flex gap-3">
                    <Phone className="w-4 h-4 mt-1 text-[#7B2CBF] shrink-0" />
                    <span className="text-white/80" data-testid="contact-info-phone">+91 9941630848, +91 9962647558</span>
                  </li>
                  <li className="flex gap-3">
                    <Globe className="w-4 h-4 mt-1 text-[#FF007F] shrink-0" />
                    <a href="https://www.ngxptechnologies.com" target="_blank" rel="noreferrer" className="hover:text-white text-white/80">www.ngxptechnologies.com</a>
                  </li>
                </ul>
              </div>
            </div>

            <div className="ngxp-card !p-0 overflow-hidden">
              <div className="flex gap-3 p-5 border-b border-gray-100">
                <MapPin className="w-5 h-5 text-[#7B2CBF] shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-display font-semibold">Kochi Office</h4>
                  <p className="text-sm text-gray-600 mt-1 leading-relaxed">
                    BB Arcade, Millumpadi Road, Nest Road, Near NEST Techpark, Kakkanad, Kochi, Kerala, India 682042
                  </p>
                </div>
              </div>
              <iframe
                title="NGXP Kochi Office"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3929.0!2d76.3416!3d10.0262!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3b080d8e0c0f0001%3A0x0!2sKakkanad%2C+Kochi%2C+Kerala+682042!5e0!3m2!1sen!2sin!4v1"
                width="100%"
                height="200"
                style={{ border: 0, display: "block" }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="map-kochi"
              />
            </div>

            <div className="ngxp-card !p-0 overflow-hidden">
              <div className="flex gap-3 p-5 border-b border-gray-100">
                <MapPin className="w-5 h-5 text-[#FF007F] shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-display font-semibold">Kollam Office</h4>
                  <p className="text-sm text-gray-600 mt-1 leading-relaxed">
                    Technopark Kollam, SBC No: 5, 1st Floor, Ashtamudi Building, Kundara, Kollam, Kerala, India 691501
                  </p>
                </div>
              </div>
              <iframe
                title="NGXP Kollam Office"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3941.0!2d76.5762!3d8.8932!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3b05e9e0c0f0001%3A0x0!2sTechnopark+Kollam%2C+Kundara%2C+Kollam%2C+Kerala+691501!5e0!3m2!1sen!2sin!4v1"
                width="100%"
                height="200"
                style={{ border: 0, display: "block" }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="map-kollam"
              />
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 5: STRATEGIC PARTNER */}
      <section className="bg-[#0A0B10] text-white py-14 md:py-20 relative overflow-hidden">
        <div className="absolute inset-0 dark-grid-pattern opacity-30" />
        <div className="glow-blob bg-[#7B2CBF]/20 w-[500px] h-[500px] -top-32 -right-20 pulse-glow" />
        <div className="container-ngxp relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-6 reveal">
              <span className="overline">Strategic Partnership</span>
              <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight">
                Looking for a <span className="gradient-text-glow">Strategic Technology Partner?</span>
              </h2>
              <p className="mt-6 text-white/70 leading-relaxed">If your requirement is still at an idea, concept, or early planning stage, you can still contact us.</p>
              <p className="mt-4 text-white/70 leading-relaxed">Many advanced systems begin with a business problem, not a ready-made specification. NGXP can help you structure the requirement, identify the right technology approach, and define a practical roadmap before development begins.</p>
              <div className="mt-8">
                <a href="#contact-form" className="btn-primary-gradient inline-flex" data-testid="contact-strategic-cta">
                  Book a Discussion <ArrowUpRight className="w-4 h-4" />
                </a>
              </div>
            </div>
            <div className="lg:col-span-6 reveal">
              <ul className="space-y-4 mt-4">
                {[
                  "We can help clarify the problem",
                  "We can identify the right technology direction",
                  "We can suggest a practical execution roadmap",
                  "We can help convert ideas into structured requirements",
                  "We can support proof-of-concept planning",
                  "We can explore long-term partnership possibilities",
                ].map((item, i) => (
                  <li key={i} className="partner-item flex items-start gap-3" style={{ animationDelay: `${i * 0.09}s` }}>
                    <CheckCircle2 className="partner-icon w-5 h-5 text-[#00A8E8] shrink-0 mt-0.5" />
                    <span className="partner-text text-white/80 leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 6: WHO WE WORK BEST WITH */}
      <section className="bg-[#F8F9FA] py-14 md:py-20">
        <div className="container-ngxp">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5 reveal">
              <span className="overline">Ideal Clients</span>
              <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-[#0A0B10]">
                Who We Work <span className="gradient-text-glow">Best With</span>
              </h2>
              <p className="mt-6 text-gray-600 leading-relaxed">NGXP is best suited for organizations that want to solve meaningful business or operational challenges using advanced technology.</p>
            </div>
            <div className="lg:col-span-7 reveal">
              <p className="text-xs font-bold text-gray-400 uppercase tracking-[0.2em] mb-6">You should contact us if you are looking to:</p>
              <ul className="space-y-3">
                {[
                  "Build a serious enterprise-grade technology system",
                  "Improve visibility across operations, teams, or assets",
                  "Use AI to reduce manual effort and improve decisions",
                  "Modernize existing enterprise platforms",
                  "Create a real-time monitoring or dashboard system",
                  "Develop a digital twin or simulation-based solution",
                  "Improve training through immersive 3D or XR experiences",
                  "Connect field activity, sensors, or operational data into digital systems",
                  "Build a scalable solution with long-term business value",
                  "Explore a strategic technology partnership",
                ].map((item, i) => (
                  <li key={i} className="choose-item flex items-start gap-3" style={{ animationDelay: `${i * 0.07}s` }}>
                    <CheckCircle2 className="choose-icon w-5 h-5 text-[#7B2CBF] shrink-0 mt-0.5" />
                    <span className="choose-text text-gray-700 leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 7: GLOBAL REACH */}
      <section className="bg-white py-14 md:py-20">
        <div className="container-ngxp">
          <div className="max-w-3xl mx-auto text-center reveal">
            <span className="overline">Global Reach</span>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-[#0A0B10]">
              Open to Global Projects, <span className="gradient-text-glow">Partnerships, and Collaboration</span>
            </h2>
            <p className="mt-6 text-gray-600 leading-relaxed">NGXP Technologies works with organizations that are serious about building future-ready systems. We are open to discussions with clients and partners across India, the Middle East, Europe, the United States, and global markets.</p>
            <p className="mt-4 text-gray-600 leading-relaxed">Whether you are a growing enterprise, established industry leader, technology company, investor, or strategic partner, we welcome meaningful conversations around advanced technology collaboration.</p>
          </div>
        </div>
      </section>

      {/* SECTION 8: WHAT HAPPENS AFTER */}
      <section className="bg-[#F8F9FA] py-14 md:py-20">
        <div className="container-ngxp">
          <div className="max-w-2xl reveal">
            <span className="overline">Our Process</span>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-[#0A0B10]">
              What Happens After <span className="gradient-text-glow">You Contact Us</span>
            </h2>
          </div>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 reveal">
            {[
              { num: "01", title: "We Review Your Requirement", desc: "Our team reviews your message, project summary, business context, and any supporting documents shared." },
              { num: "02", title: "We Understand the Objective", desc: "We identify the core problem, expected outcome, business priority, and possible technology direction." },
              { num: "03", title: "We Schedule a Discussion", desc: "If the requirement matches NGXP's capabilities, we schedule a discussion to understand the opportunity in detail." },
              { num: "04", title: "We Recommend the Next Step", desc: "Based on the discussion, we may suggest a discovery session, technical consultation, POC plan, proposal, roadmap, or partnership conversation." },
            ].map((step, i) => (
              <div key={i} className="ngxp-card reveal">
                <span className="text-3xl font-display font-bold gradient-text-glow">{step.num}</span>
                <h3 className="font-display text-lg font-semibold text-[#0A0B10] mt-3">{step.title}</h3>
                <p className="text-sm text-gray-600 mt-2 leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 10: FINAL CTA */}
      <section className="bg-white py-14 md:py-18">
        <div className="container-ngxp">
          <div className="rounded-3xl bg-gradient-to-br from-[#00A8E8] via-[#7B2CBF] to-[#FF007F] p-10 md:p-16 text-white relative overflow-hidden">
            <div className="absolute inset-0 dark-grid-pattern opacity-30" />
            <div className="relative z-10">
              <div className="max-w-3xl">
                <h3 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Have a Complex Technology Challenge? Let's Discuss It.</h3>
                <p className="mt-4 text-white/85 leading-relaxed max-w-2xl">
                  Tell us what you are trying to build, improve, monitor, automate, simulate, or transform. NGXP Technologies can help you identify the right path forward.
                </p>
              </div>
              <div className="mt-8 flex flex-wrap gap-3">
                <a href="#contact-form" data-testid="contact-final-cta-form" className="inline-flex items-center gap-2 rounded-full bg-white text-[#0A0B10] px-6 py-3 text-sm font-semibold hover:-translate-y-0.5 transition-transform">
                  Submit Your Requirement <ArrowUpRight className="w-4 h-4" />
                </a>
                <a href="#contact-form" data-testid="contact-final-cta-discuss" className="inline-flex items-center gap-2 rounded-full bg-white/20 border border-white/30 text-white px-6 py-3 text-sm font-semibold hover:-translate-y-0.5 transition-transform">
                  Start a Strategic Discussion <ArrowRight className="w-4 h-4" />
                </a>
              </div>
              <p className="mt-8 text-white/50 text-xs text-center">
                NGXP Technologies – Building future-ready systems for organizations ready to move beyond ordinary technology.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold uppercase tracking-[0.16em] text-gray-500 mb-2">{label}</span>
      {children}
    </label>
  );
}
